import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const ProjectSummaryPanel = ({
  selectedPrinter,
  printers,
  selectedFilaments,
  filaments,
  projectData,
  costs,
  isCalculating,
}) => {
  const printer = printers?.find(p => p?.id === selectedPrinter);
  const quantity = parseInt(projectData?.quantity) || 1;
  const duration = parseFloat(projectData?.duration) || 0;
  const perUnit = costs?.perUnit || 0;
  const totalProject = (costs?.finalPrice || 0) || (perUnit * quantity);
  const projectName = projectData?.name || 'Projeto sem nome';

  // Segmentos de custo com cores consistentes
  const segments = [
    { key: 'Material', value: costs?.totalMaterial || 0, color: 'bg-blue-500', icon: 'Package' },
    { key: 'Energia', value: costs?.totalElectricity || 0, color: 'bg-yellow-500', icon: 'Zap' },
    { key: 'Mão de obra', value: costs?.totalLabor || 0, color: 'bg-green-500', icon: 'Clock' },
    { key: 'Depreciação', value: costs?.totalDepreciation || 0, color: 'bg-purple-500', icon: 'TrendingDown' },
    { key: 'Impostos', value: costs?.vatAmount || 0, color: 'bg-red-500', icon: 'Percent' },
    { key: 'Marketplace', value: costs?.marketplaceFeesTotal || 0, color: 'bg-indigo-500', icon: 'ShoppingCart' },
    { key: 'Outros', value: costs?.otherCosts || 0, color: 'bg-gray-500', icon: 'Plus' },
  ];

  const nonZeroSegments = segments.filter(s => (s.value || 0) > 0);
  const totalCosts = nonZeroSegments.reduce((sum, s) => sum + (s.value || 0), 0);
  const hasData = totalCosts > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border rounded-xl shadow-sm overflow-hidden"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-primary/5 to-primary/10 border-b border-border px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="BarChart3" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Resumo do Projeto</h3>
              <p className="text-sm text-muted-foreground">Análise de custos e distribuição</p>
            </div>
          </div>
          {isCalculating && (
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <span>Calculando...</span>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Project Info */}
        <div className="space-y-4">
          <div>
            <h4 className="text-base font-semibold text-foreground mb-2">{projectName}</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <Icon name="Hash" size={14} className="text-muted-foreground" />
                <span className="text-muted-foreground">Quantidade:</span>
                <span className="font-medium text-foreground">{quantity}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={14} className="text-muted-foreground" />
                <span className="text-muted-foreground">Duração:</span>
                <span className="font-medium text-foreground">{duration ? `${duration}h` : '—'}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Printer" size={14} className="text-muted-foreground" />
                <span className="text-muted-foreground">Impressora:</span>
                <span className="font-medium text-foreground truncate">{printer?.name || 'Não selecionada'}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Layers" size={14} className="text-muted-foreground" />
                <span className="text-muted-foreground">Filamentos:</span>
                <span className="font-medium text-foreground">{selectedFilaments?.length || 0}</span>
              </div>
            </div>
          </div>

          {/* Price Summary */}
          <div className="bg-muted/30 rounded-xl p-4 border border-border/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Valor Total do Projeto</span>
              <div className="text-right">
                <div className="text-2xl font-bold text-primary">
                  R$ {hasData ? totalProject.toFixed(2) : '0,00'}
                </div>
                <div className="text-sm text-muted-foreground">
                  R$ {hasData ? perUnit.toFixed(2) : '0,00'} por peça
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cost Breakdown */}
        {hasData ? (
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Icon name="PieChart" size={16} className="text-muted-foreground" />
              <h4 className="text-sm font-semibold text-foreground">Distribuição de Custos</h4>
            </div>

            {/* Visual Bar */}
            <div className="w-full h-6 rounded-xl bg-muted border border-border overflow-hidden flex">
              {nonZeroSegments.map((segment, index) => {
                const percentage = (segment.value / totalCosts) * 100;
                const minWidth = percentage < 5 ? 5 : percentage; // Minimum width for visibility
                
                return (
                  <div
                    key={segment.key}
                    className={`${segment.color} relative flex items-center justify-center`}
                    style={{ width: `${minWidth}%` }}
                    title={`${segment.key}: R$ ${segment.value.toFixed(2)} (${percentage.toFixed(1)}%)`}
                  >
                    {percentage >= 10 && (
                      <span className="text-xs font-semibold text-white">
                        {Math.round(percentage)}%
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Cost Items */}
            <div className="space-y-3">
              {nonZeroSegments.map((segment) => {
                const percentage = (segment.value / totalCosts) * 100;
                
                return (
                  <div key={segment.key} className="flex items-center justify-between p-3 bg-muted/20 rounded-xl border border-border/30">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${segment.color}`} />
                      <div className="flex items-center space-x-2">
                        <Icon name={segment.icon} size={14} className="text-muted-foreground" />
                        <span className="text-sm font-medium text-foreground">{segment.key}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-foreground">
                        R$ {segment.value.toFixed(2)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {percentage.toFixed(1)}%
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Profit Margin Info */}
            {projectData?.clientMarkup && (
              <div className="mt-4 p-3 bg-primary/5 rounded-xl border border-primary/20">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2">
                    <Icon name="TrendingUp" size={14} className="text-primary" />
                    <span className="text-foreground font-medium">Margem de Lucro</span>
                  </div>
                  <span className="font-semibold text-primary">{projectData.clientMarkup}%</span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-muted/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Calculator" size={24} className="text-muted-foreground" />
            </div>
            <h4 className="text-sm font-medium text-foreground mb-2">Aguardando Cálculo</h4>
            <p className="text-xs text-muted-foreground max-w-xs mx-auto">
              Configure os parâmetros do projeto e clique em "Calcular Custos" para ver o resumo detalhado.
            </p>
          </div>
        )}

        {/* Quick Actions */}
        {hasData && (
          <div className="pt-4 border-t border-border">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Última atualização: agora</span>
              <div className="flex items-center space-x-1">
                <Icon name="RefreshCw" size={12} />
                <span>Atualização automática</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ProjectSummaryPanel;